import { create } from "zustand";
import { devtools } from "zustand/middleware";
import axios from "axios";
import { produce } from "immer";
import { findIndex } from "lodash";
import fileDownload from "js-file-download";

const initialState = {
  gettingTrialBalance: false,
  trialBalanceError: {},
  trialBalance: [],
  downloadingTrialBalance: false,
  downloadTrialBalanceError: {},
  // project-level
  gettingProjectTrialBalance: false,
  projectTrialBalanceError: {},
  projectTrialBalance: [],
  downloadingProjectTrialBalance: false,
  downloadProjectTrialBalanceError: {},
};

const store = (set, get) => ({
  ...initialState,

  async getTrialBalance(context) {
    set({ gettingTrialBalance: true, trialBalanceError: {} });
    try {
      // Remove any trailing slash to avoid 404 errors
      const endpoint = "/finance/trial-balance".replace(/\/$/, "");
      const res = await axios.get(endpoint, {
        params: {
          asOfDate: context.asOfDate,
          startDate: context.startDate,
          endDate: context.endDate,
        },
      });
      
      // Handle response structure - http.serverSuccess wraps the response
      // Backend returns: { success: true, message: "...", data: { trialBalance: [...], totals: {...} } }
      // Axios interceptor returns response.data, so res is the wrapped object
      // We need to extract res.data to get the actual trial balance data
      const responseData = res?.data || res;
      
      // Log for debugging
      console.log("Trial Balance Response:", responseData);
      
      const records = get().trialBalance;
      const index = findIndex(
        records,
        (record) => record.asOfDate === context.asOfDate
      );

      if (index > -1) {
        set(
          produce((state) => {
            state.trialBalance[index] = {
              data: responseData,
              ...context,
            };
          })
        );
      } else {
        set(
          produce((state) => {
            state.trialBalance.push({
              data: responseData,
              ...context,
            });
          })
        );
      }
    } catch (err) {
      console.error("Trial Balance API Error:", err);
      console.error("Error Details:", {
        status: err?.response?.status,
        statusText: err?.response?.statusText,
        data: err?.response?.data,
        message: err?.message,
      });
      console.error("Request URL:", `/finance/trial-balance?asOfDate=${context.asOfDate}`);
      set({ trialBalanceError: err });
    } finally {
      set({ gettingTrialBalance: false });
    }
  },

  async getProjectTrialBalance(context) {
    set({ gettingProjectTrialBalance: true, projectTrialBalanceError: {} });
    try {
      const endpoint = `/finance/trial-balance/project/${context.projectId}`.replace(
        /\/$/,
        ""
      );
      const res = await axios.get(endpoint, {
        params: {
          asOfDate: context.asOfDate,
          startDate: context.startDate,
          endDate: context.endDate,
        },
      });

      const responseData = res?.data || res;

      const records = get().projectTrialBalance;
      const index = findIndex(
        records,
        (record) => {
          // Match by startDate/endDate if provided, otherwise by asOfDate
          if (context.startDate && context.endDate) {
            return (
              record.startDate === context.startDate &&
              record.endDate === context.endDate &&
              record.projectId === context.projectId
            );
          } else {
            return (
              record.asOfDate === context.asOfDate &&
              record.projectId === context.projectId
            );
          }
        }
      );

      if (index > -1) {
        set(
          produce((state) => {
            state.projectTrialBalance[index] = {
              data: responseData,
              ...context,
            };
          })
        );
      } else {
        set(
          produce((state) => {
            state.projectTrialBalance.push({
              data: responseData,
              ...context,
            });
          })
        );
      }
    } catch (err) {
      console.error("Project Trial Balance API Error:", err);
      set({ projectTrialBalanceError: err });
    } finally {
      set({ gettingProjectTrialBalance: false });
    }
  },

  async downloadTrialBalance(context, fileName = "TRIAL_BALANCE.xlsx") {
    set({ downloadingTrialBalance: true, downloadTrialBalanceError: {} });
    await axios
      .get("/finance/trial-balance/download-excel", {
        params: {
          asOfDate: context.asOfDate,
        },
        responseType: "blob",
      })
      .then((res) => {
        fileDownload(res.data, fileName);
      })
      .catch((err) => {
        set({ downloadTrialBalanceError: err });
      })
      .finally(() => set({ downloadingTrialBalance: false }));
  },

  async downloadProjectTrialBalance(
    context,
    fileName = "PROJECT_TRIAL_BALANCE.xlsx"
  ) {
    set({
      downloadingProjectTrialBalance: true,
      downloadProjectTrialBalanceError: {},
    });
    await axios
      .get(`/finance/trial-balance/download-excel`, {
        params: {
          asOfDate: context.asOfDate,
          projectId: context.projectId,
        },
        responseType: "blob",
      })
      .then((res) => {
        fileDownload(res.data, fileName);
      })
      .catch((err) => {
        set({ downloadProjectTrialBalanceError: err });
      })
      .finally(() => set({ downloadingProjectTrialBalance: false }));
  },
});

const storeName = "Trial Balance Store";

const useTrialBalanceStore = create(devtools(store, { storeName }), { storeName });

export default useTrialBalanceStore;

